export type CallType = 'audio' | 'video';

/**
 * Browser event emitted by project surfaces which do not own the ChatDock.
 * ChatDock is the only consumer: it resolves the requested project against
 * the caller's currently authorized project/member data before starting a
 * call.
 */
export const OPEN_PROJECT_CALL_EVENT = 'open-project-call';

export interface OpenProjectCallDetail {
  projectId: string;
  type: 'video';
}

export interface ProjectCallIntent extends OpenProjectCallDetail {
  /** Lets the UI consume exactly the request it handled. */
  requestId: number;
}

/**
 * Synchronous, one-slot bridge queue. React state is deliberately not used as
 * the authority here: two browser events can arrive before a render commits.
 * The caller either accepts exactly one intent or receives an explicit busy
 * result; it never silently replaces a project request.
 */
export interface ProjectCallIntentQueue {
  tryAccept(detail: OpenProjectCallDetail): ProjectCallIntent | null;
  consume(requestId: number): boolean;
}

export function createProjectCallIntentQueue(): ProjectCallIntentQueue {
  let pending: ProjectCallIntent | null = null;
  let nextRequestId = 0;
  return {
    tryAccept(detail) {
      if (pending) return null;
      nextRequestId += 1;
      pending = { ...detail, requestId: nextRequestId };
      return pending;
    },
    consume(requestId) {
      if (pending?.requestId !== requestId) return false;
      pending = null;
      return true;
    },
  };
}

export interface ProjectCallIntentConsumer {
  /** Claims an intent once; repeated renders/refetches return false. */
  claim(requestId: number): boolean;
}

export function createProjectCallIntentConsumer(): ProjectCallIntentConsumer {
  let mostRecentlyClaimedId: number | null = null;
  return {
    claim(requestId) {
      if (mostRecentlyClaimedId === requestId) return false;
      mostRecentlyClaimedId = requestId;
      return true;
    },
  };
}

export interface ProjectCallCandidate {
  projectId: string;
  existingChannel: { id: string } | null;
  /** Other users assigned to the project (lead + artists), caller excluded. */
  assignedArtists: { id: number }[];
  /**
   * Active admins who are NOT already in `assignedArtists` and are not the
   * caller. Admins are auto-members of every project channel, so they can
   * always be invited even when nobody is assigned to the project.
   */
  admins?: { id: number }[];
}

export function candidateAdmins(candidate: ProjectCallCandidate): { id: number }[] {
  return candidate.admins ?? [];
}

export interface ArtistPopoverState {
  projectId: string;
  type: CallType;
}

export const MAX_CALL_PARTICIPANTS = 4;

export const GROUP_CALL_LIMIT_TOAST = {
  title: 'Group call limit',
  description: `Up to ${MAX_CALL_PARTICIPANTS} people can be in a call (V1).`,
  variant: 'destructive' as const,
};

export const NO_CHANNEL_TOAST = {
  title: 'Could not start call',
  description: 'Failed to open project channel.',
  variant: 'destructive' as const,
};

export type RowCallAction =
  | { kind: 'noop' }
  | { kind: 'start'; inviteeUserIds: number[] }
  | { kind: 'open-popover'; projectId: string; type: CallType }
  | { kind: 'close-popover' };

export function decideProjectRowCallAction(
  candidate: ProjectCallCandidate,
  type: CallType,
  currentPopover: ArtistPopoverState | null,
): RowCallAction {
  const admins = candidateAdmins(candidate);
  if (candidate.assignedArtists.length === 0) {
    // Nobody else is assigned: ring the admins directly (no question to ask).
    if (admins.length === 0) return { kind: 'noop' };
    // More admins than fit into one call → let the caller pick in the popover
    // instead of dead-ending on the limit toast.
    if (!exceedsCallParticipantLimit(admins)) {
      return { kind: 'start', inviteeUserIds: admins.map((a) => a.id) };
    }
  } else if (candidate.assignedArtists.length === 1 && admins.length === 0) {
    return { kind: 'start', inviteeUserIds: [candidate.assignedArtists[0].id] };
  }
  // At least one other assignee AND admins available → always ask via the
  // popover whether the admins should be included (checked by default).
  if (
    currentPopover &&
    currentPopover.projectId === candidate.projectId &&
    currentPopover.type === type
  ) {
    return { kind: 'close-popover' };
  }
  return { kind: 'open-popover', projectId: candidate.projectId, type };
}

export type ProjectCallIntentResolution<C extends ProjectCallCandidate> =
  | { kind: 'wait' }
  | { kind: 'unavailable' }
  | { kind: 'no-invitees'; candidate: C }
  | { kind: 'dispatch'; candidate: C; type: CallType; opensRecipientPicker: boolean };

/**
 * Resolve an external project-call request without ever falling back to the
 * currently selected project. Keeping this small decision point pure makes it
 * safe to retry while queries load, while the caller consumes a request once
 * it receives any terminal result.
 */
export function resolveProjectCallIntent<C extends ProjectCallCandidate>(
  intent: ProjectCallIntent,
  candidates: readonly C[],
  dataReady: boolean,
): ProjectCallIntentResolution<C> {
  if (!dataReady) return { kind: 'wait' };

  const candidate = candidates.find((item) => item.projectId === intent.projectId);
  if (!candidate) return { kind: 'unavailable' };

  // An explicit shortcut must initiate a call, not toggle an already-open
  // row popover. Passing a null popover here gives the same recipient rules as
  // a fresh row click.
  const action = decideProjectRowCallAction(candidate, intent.type, null);
  if (action.kind === 'noop') return { kind: 'no-invitees', candidate };

  return {
    kind: 'dispatch',
    candidate,
    type: intent.type,
    opensRecipientPicker: action.kind === 'open-popover',
  };
}

export function exceedsCallParticipantLimit(inviteeUserIds: readonly unknown[]): boolean {
  return inviteeUserIds.length + 1 > MAX_CALL_PARTICIPANTS;
}

/**
 * Combine the chosen assignee ids with the admins still ticked in the popover.
 * `includedAdminIds` defaults to ALL candidate admins (default on); only ids
 * that are real candidate admins survive (defense-in-depth like
 * filterAllowedInvitees).
 */
export function withIncludedAdmins(
  candidate: ProjectCallCandidate,
  inviteeUserIds: readonly number[],
  includedAdminIds?: Iterable<number>,
): number[] {
  const admins = candidateAdmins(candidate);
  const adminIds = new Set(admins.map((a) => a.id));
  const included = includedAdminIds === undefined
    ? adminIds
    : new Set(Array.from(includedAdminIds).filter((id) => adminIds.has(id)));
  const out: number[] = [];
  const seen = new Set<number>();
  for (const id of [...inviteeUserIds, ...admins.map((a) => a.id).filter((id) => included.has(id))]) {
    if (!seen.has(id)) { seen.add(id); out.push(id); }
  }
  return out;
}

export function filterAllowedInvitees(
  assignedArtists: readonly { id: number }[],
  selectedUserIds: Iterable<number>,
): number[] {
  const allowed = new Set(assignedArtists.map((a) => a.id));
  return Array.from(selectedUserIds).filter((id) => allowed.has(id));
}

export function toggleSelectedUserId(
  prev: ReadonlySet<number>,
  artistId: number,
): Set<number> {
  const next = new Set(prev);
  if (next.has(artistId)) next.delete(artistId);
  else next.add(artistId);
  return next;
}

export interface StartProjectChannelCallDeps<C extends ProjectCallCandidate = ProjectCallCandidate> {
  ensureChannelId: (candidate: C) => Promise<string | null>;
  startCall: (args: {
    channelId: string;
    type: CallType;
    inviteeUserIds: number[];
    /** Originating project id — Phase B (#160) preselects the chip. */
    projectId?: string | null;
  }) => Promise<void> | void;
  toast: (msg: { title: string; description: string; variant?: 'default' | 'destructive' }) => void;
}

export type StartProjectChannelCallResult =
  | 'noop'
  | 'limited'
  | 'no-channel'
  | 'started';

export async function performStartProjectChannelCall<C extends ProjectCallCandidate>(
  candidate: C,
  type: CallType,
  inviteeUserIds: number[],
  deps: StartProjectChannelCallDeps<C>,
): Promise<StartProjectChannelCallResult> {
  if (inviteeUserIds.length === 0) return 'noop';
  if (exceedsCallParticipantLimit(inviteeUserIds)) {
    deps.toast(GROUP_CALL_LIMIT_TOAST);
    return 'limited';
  }
  const channelId = await deps.ensureChannelId(candidate);
  if (!channelId) {
    deps.toast(NO_CHANNEL_TOAST);
    return 'no-channel';
  }
  // Project-channel calls always carry the originating project id so the
  // in-call chip is preselected the moment the lobby resolves.
  await deps.startCall({ channelId, type, inviteeUserIds, projectId: candidate.projectId });
  return 'started';
}

// ---------------------------------------------------------------------------
// Group call from the channel-picker (ChannelPickerButton.startGroupCall).
//
// Direct (1:1) channels can't host a group call, and there may be no channel
// open at all. In both cases we fall back to the global channel. The resolved
// channel is then passed DIRECTLY into the call so it can't fire against stale
// UI state — onChannelSelect only updates what the panel displays and its
// state won't have propagated by the time startCall runs.
// ---------------------------------------------------------------------------

export interface GroupCallChannel {
  id: string;
  type?: string | null;
  projectId?: string | null;
}

export const NO_GROUP_CHANNEL_TOAST = {
  title: 'Open a group channel',
  description: 'Open a project, topic or global channel to start a group call.',
  variant: 'destructive' as const,
};

/**
 * Resolve which channel a group call should start in. Direct channels and a
 * missing channel both fall back to the global channel. Returns null when no
 * usable channel exists (nothing group-capable open AND no global channel).
 */
export function resolveGroupCallChannel<C extends GroupCallChannel>(
  currentChannel: C | null | undefined,
  globalChannel: C | null | undefined,
): C | null {
  const target = !currentChannel || currentChannel.type === 'direct' ? globalChannel : currentChannel;
  return target ?? null;
}

export interface StartGroupCallDeps<C extends GroupCallChannel> {
  startCall: (args: {
    channelId: string;
    type: CallType;
    inviteeUserIds: number[];
    /** Inherited from the resolved channel (project channels only). */
    projectId?: string | null;
  }) => Promise<void> | void;
  /** Switch the panel to the resolved channel (display-only, async state). */
  onChannelSelect: (channelId: string) => void;
  /** Clear the selected-user set once the call is committed. */
  clearSelection: () => void;
  toast: (msg: { title: string; description: string; variant?: 'default' | 'destructive' }) => void;
}

export type StartGroupCallResult = 'noop' | 'limited' | 'no-channel' | 'started';

export async function performStartGroupCall<C extends GroupCallChannel>(
  currentChannel: C | null | undefined,
  globalChannel: C | null | undefined,
  type: CallType,
  inviteeUserIds: number[],
  deps: StartGroupCallDeps<C>,
): Promise<StartGroupCallResult> {
  if (inviteeUserIds.length === 0) return 'noop';
  if (exceedsCallParticipantLimit(inviteeUserIds)) {
    deps.toast(GROUP_CALL_LIMIT_TOAST);
    return 'limited';
  }
  const targetChannel = resolveGroupCallChannel(currentChannel, globalChannel);
  if (!targetChannel) {
    deps.toast(NO_GROUP_CHANNEL_TOAST);
    return 'no-channel';
  }
  deps.clearSelection();
  // Only switch the panel when we're actually changing channels.
  if (targetChannel.id !== currentChannel?.id) {
    deps.onChannelSelect(targetChannel.id);
  }
  // Pass the resolved channel id DIRECTLY — never re-read the panel's channel,
  // which onChannelSelect has only just (asynchronously) updated.
  await deps.startCall({
    channelId: targetChannel.id,
    type,
    inviteeUserIds,
    projectId: targetChannel.projectId ?? undefined,
  });
  return 'started';
}
